import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';
import { setLoadingAction } from '../techniques/techniquesSlice';

const initialState = {
  items: {},  // 使用对象来存储不同 classId 的笔记
  loading: false,
  error: null,
};

export const fetchNotes = createAsyncThunk(
  'notes/fetchNotes',
  async (classId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/techniques/notes/?class_id=${classId}`);
      console.log('Fetched notes:', response.data);
      return { classId, notes: response.data };
    } catch (error) {
      console.error('Error fetching notes:', error);
      return rejectWithValue(error.response?.data || 'Failed to fetch notes');
    }
  }
);

export const addNote = createAsyncThunk(
  'notes/addNote',
  async ({ classId, content }, { dispatch }) => {
    dispatch(setLoadingAction({ classId: `notes_${classId}`, isLoading: true }));
    try {
      const response = await api.post('/techniques/notes/', {
        class_item: classId,
        content
      });
      return response.data;
    } finally {
      dispatch(setLoadingAction({ classId: `notes_${classId}`, isLoading: false }));
    }
  }
);

export const createNote = createAsyncThunk(
  'notes/createNote',
  async ({ content, classId }, { rejectWithValue, getState }) => {
    try {
      const response = await api.post('/techniques/notes/', {
        content,
        class_item: classId
      });

      const user = getState().auth.user;
      console.log('Create note response:', response);  // Debug log

      return { 
        classId, 
        note: {
          ...response.data,  // Use response.data instead of response
          class_item: classId,
          username: user?.username
        }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create note');
    }
  }
);

export const updateNote = createAsyncThunk(
  'notes/updateNote',
  async ({ noteId, content, classId }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/techniques/notes/${noteId}/`, {
        content,
        class_item: classId
      });
      return { classId, note: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update note');
    }
  }
);

export const deleteNote = createAsyncThunk(
  'notes/deleteNote',
  async ({ noteId, classId }, { rejectWithValue }) => {
    try {
      await api.delete(`/techniques/notes/${noteId}/`);
      return { noteId, classId };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete note');
    }
  }
);

const notesSlice = createSlice({
  name: 'notes',
  initialState,
  reducers: {
    clearNotesError: (state) => {
      state.error = null;
    },
    clearNotesLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotes.fulfilled, (state, action) => {
        state.loading = false;
        state.items[action.payload.classId] = action.payload.notes;
        console.log('DEBUG - Redux Store Update:', {
          classId: action.payload.classId,
          notesCount: action.payload.notes.length,
          allUsernames: action.payload.notes.map(note => note.username),
          storeState: state.items,  // 查看完整的 store 状态
        });
      })
      .addCase(fetchNotes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('DEBUG - Fetch Notes Rejected:', action.payload);
      })
      .addCase(createNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNote.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.items[action.payload.classId]) {
          state.items[action.payload.classId] = [];
        }
        // 只有当笔记属于当前 class 时才添加
        if (action.payload.note.class_item === parseInt(action.payload.classId)) {
          state.items[action.payload.classId].push(action.payload.note);
        }
      })
      .addCase(createNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateNote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        const updatedNote = action.payload;
        const classId = updatedNote.class_item;
        if (state.items[classId]) {
          const index = state.items[classId].findIndex(note => note.id === updatedNote.id);
          if (index !== -1) {
            state.items[classId][index] = updatedNote;
          }
        }
        state.loading = false;
      })
      .addCase(updateNote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update note';
      })
      .addCase(deleteNote.fulfilled, (state, action) => {
        Object.keys(state.items).forEach(classId => {
          state.items[classId] = state.items[classId].filter(note => note.id !== action.payload.noteId);
        });
      });
  },
});

export const { clearNotesError, clearNotesLoading } = notesSlice.actions;
export default notesSlice.reducer;